import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Video, Check, Sparkles, Zap, TrendingUp, Users } from "lucide-react";

const Sora2Video = () => {
  const features = [
    {
      icon: Video,
      title: "Done-for-You Video Creation",
      description: "You give us the message, offer, or idea — we handle the scripting, voiceovers, editing, and design using advanced AI tools."
    },
    {
      icon: Sparkles,
      title: "Branded & Personalized",
      description: "Every video includes your logo, colors, and brand style so your content stays consistent and recognizable."
    },
    {
      icon: Zap,
      title: "AI-Powered Voiceovers & Visuals",
      description: "Choose from realistic AI narrators or on-screen presenters — no filming or expensive equipment required."
    },
    {
      icon: TrendingUp,
      title: "Optimized for Every Platform",
      description: "We deliver videos in the perfect formats for Facebook, Instagram, TikTok, YouTube, and more — ready to post instantly."
    }
  ];

  const benefits = [
    "Consistent, fresh content every week",
    "Professional-quality videos done for you",
    "Faster engagement and conversions online",
    "A complete social media content system powered by AI"
  ];

  const includes = [
    "Up to 30 fully customized videos created for your brand",
    "Unlimited script revisions & content guidance",
    "Access to Neil AI's creative team and AI tools",
    "Fast delivery — ready-to-post videos every week"
  ];

  const perfectFor = [
    "Small businesses and local brands needing regular marketing videos",
    "Agencies that want to scale content fast",
    "Sales teams looking for video ads and lead follow-ups",
    "Companies wanting to grow on social media with minimal effort"
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Sora-2 AI Video Creator - Neil AI Global Solutions"
        description="Professional AI video creation service. Up to 30 custom videos per month for your brand. Done-for-you video marketing automation."
        keywords="AI video creator, Sora-2, video marketing, social media videos, automated video production, video ads"
      />
      <Navigation />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 py-16 text-center">
          <Badge className="mb-4 text-lg px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500">
            🎥 Sora-2 AI Video Creator
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 bg-clip-text text-transparent">
            We Create Engaging AI Videos for You
          </h1>
          <p className="text-2xl text-foreground font-semibold mb-4">
            Up to 30 Videos Per Month
          </p>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Let us handle your video marketing while you focus on growing your business. With Sora-2 AI Video Creator, the Neil AI team produces up to 30 high-quality, ready-to-post videos every month — crafted to match your brand, voice, and audience.
          </p>
          <div className="flex gap-4 justify-center flex-wrap mb-8">
            <Button size="lg" className="text-lg bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600" asChild>
              <a href="https://link.fastpaydirect.com/payment-link/68938782ddc6a61a45c5d90a">
                Start Your Plan - $135/mo
              </a>
            </Button>
            <Button size="lg" variant="outline" className="text-lg" asChild>
              <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics">
                Schedule a Demo
              </a>
            </Button>
          </div>
          <p className="text-muted-foreground">
            💡 Our AI-powered video system combines creativity with automation
          </p>
        </section>

        {/* What We Do Section */}
        <section className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">⚡ What We Do for You</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-2 border-primary/20 hover:border-primary/40 transition-colors">
                  <CardHeader>
                    <div className="h-14 w-14 rounded-lg bg-gradient-to-br from-purple-500/10 to-pink-500/10 flex items-center justify-center mb-4 mx-auto">
                      <Icon className="h-7 w-7 text-purple-500" />
                    </div>
                    <CardTitle className="text-lg text-center">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground text-center">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Value Section */}
        <section className="container mx-auto px-4 py-16 bg-muted/30 rounded-2xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">💰 The Value for Your Business</h2>
            <p className="text-xl text-muted-foreground text-center mb-8">
              Hiring videographers, editors, and marketers can cost thousands — and take weeks. With Neil AI's Sora-2 service, you get:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <Card key={index} className="border-2 border-primary/20">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <p className="text-foreground font-medium">{benefit}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Perfect For Section */}
        <section className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">🔹 Who It's Perfect For</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {perfectFor.map((item, index) => (
                <Card key={index} className="border-2 border-primary/20 hover:border-primary/40 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Users className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <p className="text-foreground">{item}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What's Included Section */}
        <section className="container mx-auto px-4 py-16 bg-gradient-to-br from-purple-500/5 to-pink-500/5 rounded-2xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">📦 Your Monthly Plan Includes</h2>
            <div className="grid gap-4">
              {includes.map((item, index) => (
                <Card key={index} className="border-2 border-primary/20">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                        ✓
                      </div>
                      <p className="text-foreground font-medium text-lg">{item}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-4 py-16">
          <Card className="max-w-4xl mx-auto text-center border-4 border-purple-500/30 bg-gradient-to-br from-purple-500/5 to-pink-500/5">
            <CardHeader>
              <CardTitle className="text-3xl md:text-4xl font-bold mb-4">
                🚀 Let Neil AI Create Your Videos While You Focus on Results
              </CardTitle>
              <p className="text-xl text-muted-foreground">
                We make the videos. You post them, grow your audience, and close more sales. Start your Sora-2 AI Video Creator plan today and get your first videos within days.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex gap-4 justify-center flex-wrap">
                <Button size="lg" className="text-lg bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600" asChild>
                  <a href="https://link.fastpaydirect.com/payment-link/68938782ddc6a61a45c5d90a">
                    Get Started - $135/mo
                  </a>
                </Button>
                <Button size="lg" variant="secondary" className="text-lg" asChild>
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics">
                    Schedule Free Demo
                  </a>
                </Button>
              </div>
              <div className="pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground mb-2 font-semibold">
                  Available as an add-on to any Neil AI Automation Plan
                </p>
                <Button variant="link" asChild>
                  <a href="/ai-automation#pricing">View All Automation Plans →</a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Sora2Video;
